import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts'
import { AlertTriangle } from 'lucide-react'

interface ProfitChartProps {
  costiDiretti: number
  costoPersonale: number
  costoMezzi: number
  costoMagazzino: number
  totaleValore: number
  margineEffettivo: number
  margineDesiderato: number
}

export function ProfitChart({
  costiDiretti,
  costoPersonale,
  costoMezzi,
  costoMagazzino,
  totaleValore,
  margineEffettivo,
  margineDesiderato
}: ProfitChartProps) {
  // Converti i valori in numeri sicuri
  const costiDir = Number(costiDiretti) || 0
  const costoPers = Number(costoPersonale) || 0
  const costoMez = Number(costoMezzi) || 0
  const costoMag = Number(costoMagazzino) || 0
  const totValore = Number(totaleValore) || 0

  const totaleCosti = costiDir + costoPers + costoMez + costoMag
  const profitto = totValore - totaleCosti

  // Se non ci sono valori, mostra un placeholder
  if (totValore <= 0) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-400">
        Aggiungi voci per vedere il grafico
      </div>
    )
  }

  const data = [
    { name: 'Costi Diretti', value: Math.max(0, costiDir), color: '#ef4444' },
    { name: 'Personale', value: Math.max(0, costoPers), color: '#f97316' },
    { name: 'Mezzi', value: Math.max(0, costoMez), color: '#eab308' },
    { name: 'Articoli/Magazzino', value: Math.max(0, costoMag), color: '#14b8a6' },
    { name: 'Profitto', value: Math.max(0, profitto), color: profitto >= 0 ? '#22c55e' : '#dc2626' },
  ].filter(item => item.value > 0)

  const margineEff = Number(margineEffettivo) || 0
  const margineDes = Number(margineDesiderato) || 20
  const isAlert = margineEff < margineDes

  const formatCurrency = (value: number | string) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(Number(value) || 0)
  }

  return (
    <div className="relative">
      {isAlert && (
        <div className="absolute top-0 right-0 z-10 flex items-center gap-2 bg-red-100 text-red-700 px-3 py-1.5 rounded-lg text-sm font-medium animate-pulse">
          <AlertTriangle className="w-4 h-4" />
          Margine sotto soglia!
        </div>
      )}

      <ResponsiveContainer width="100%" height={250}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={50}
            outerRadius={80}
            paddingAngle={2}
            dataKey="value"
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip
            formatter={(value) => formatCurrency(value as number)}
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
          />
          <Legend
            formatter={(value) => <span className="text-sm text-gray-600">{value}</span>}
          />
        </PieChart>
      </ResponsiveContainer>

      <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
        <div className="bg-gray-50 p-3 rounded-lg">
          <div className="text-gray-500">Totale Valore</div>
          <div className="text-lg font-semibold text-gray-900">{formatCurrency(totValore)}</div>
        </div>
        <div className="bg-gray-50 p-3 rounded-lg">
          <div className="text-gray-500">Totale Costi</div>
          <div className="text-lg font-semibold text-gray-900">{formatCurrency(totaleCosti)}</div>
        </div>
        <div className={`p-3 rounded-lg ${isAlert ? 'bg-red-50' : 'bg-green-50'}`}>
          <div className={isAlert ? 'text-red-600' : 'text-green-600'}>Margine Effettivo</div>
          <div className={`text-lg font-semibold ${isAlert ? 'text-red-700' : 'text-green-700'}`}>
            {margineEff.toFixed(1)}%
          </div>
        </div>
        <div className="bg-blue-50 p-3 rounded-lg">
          <div className="text-blue-600">Margine Desiderato</div>
          <div className="text-lg font-semibold text-blue-700">{margineDes.toFixed(1)}%</div>
        </div>
      </div>
    </div>
  )
}
